import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { usersApi } from '@/api/users';
import { coursesApi } from '@/api/courses';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { StudentDevicesCard } from '@/components/staff/StudentDevicesCard';
import { StudentPointsCard } from '@/components/staff/StudentPointsCard';
import { humanizeError } from '@/lib/errors';
import { StudentFormModal } from './StudentFormModal';
import { CuratorSubtypesCard } from './curator/CuratorSubtypesCard';
import { CuratorCard } from './curator/CuratorCard';
import { StudentEnrollmentsSummary } from './transfer/StudentEnrollmentsSummary';
import { TransferJournal } from './transfer/TransferJournal';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { isStoreBuild } from '@/lib/platform';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { detailSections } from '@/lib/curatorCardActions';
import { listAllMarathonsPaged } from '@/lib/enrollmentTransfer';
import {
  accessDefaultDays,
  defaultExtendDays,
  extrasSkippedReasons,
  lifecycleErrorText,
  pickedEnrollmentId,
} from '@/lib/enrollmentActions';
import { extendButtonLabel } from './extendButtonLabel';
import { useAuthStore } from '@/stores/auth';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MTitle } from '@/components/mobile/MobileKit';

// Status and role labels are built inside the component with t() calls.

export function AdminStudentDetailPage() {
  const ask = useConfirm();
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [headSheet, setHeadSheet] = useState(false);

  const STATUS_LABELS: Record<string, string> = {
    active: t('adminStudents.statusActive'),
    deactivated: t('adminStudents.statusDeactivated'),
    blocked: t('adminStudents.statusBlocked'),
    deleted: t('adminStudents.statusDeleted'),
  };
  const ROLE_LABELS: Record<string, string> = {
    student: t('adminDetail.roleStudent'),
    curator: t('adminDetail.roleCurator'),
    manager: t('adminDetail.roleManager'),
    admin: t('adminDetail.roleAdmin'),
  };
  const [editOpen, setEditOpen] = useState(false);
  // Число в «Продлить», вписанное админом, — для той записи, где вписано. Пока не вписано — расчёт «до
  // access_from + 90 / 365» (defaultExtendDays); до 11.10.2026 здесь стояло 30 всегда.
  const [extendTyped, setExtendTyped] = useState<{ id: string; days: number } | null>(null);
  const [pickedEnrollment, setPickedEnrollment] = useState<string>('');
  const [extendErr, setExtendErr] = useState<string | null>(null);
  const [showCreds, setShowCreds] = useState(false);
  const [invite, setInvite] = useState<{ email: string; password: string } | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [tariffPick, setTariffPick] = useState<string>('');

  const viewerRole = useAuthStore((s) => s.user?.role);
  const user = useQuery({ queryKey: ['admin', 'student', userId], queryFn: () => usersApi.get(userId!), enabled: !!userId });
  // У куратора ученических блоков нет (решение владельца 28.09.2026) — и их запросы не нужны.
  const isCurator = user.data?.role === 'curator';
  const enrollments = useQuery({
    queryKey: ['admin', 'student', userId, 'enrollments'],
    queryFn: () => usersApi.listUserEnrollments(userId!),
    enabled: !!userId && !!user.data && !isCurator,
  });
  // Названия и подтипы курсов — все рабочие курсы, постранично: сервер отдаёт не больше 100 за раз, а цель переноса
  // ищется по подтипу среди всех.
  const marathonsList = useQuery({
    queryKey: ['admin', 'marathons', 'all-paged'],
    queryFn: () =>
      listAllMarathonsPaged((offset, limit) => coursesApi.listAllMarathons({ offset, limit, template: 'false' })),
    enabled: !!user.data && !isCurator,
  });
  const marathonMap = new Map((marathonsList.data ?? []).map((m) => [m.id, m.title]));
  const marathonCategory = new Map((marathonsList.data ?? []).map((m) => [m.id, m.access_category ?? null]));
  const block = useMutation({
    mutationFn: () => usersApi.block(userId!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'student'] }),
  });
  const unblock = useMutation({
    mutationFn: () => usersApi.unblock(userId!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'student'] }),
  });
  const extend = useMutation({
    // ⚠️ Та же запись, что показывает выпадашка (`effectivePick`), а не сырое состояние выбора: при одной записи
    // выпадашки нет, и `pickedEnrollment` пуст — уходил `POST /admin/enrollments//extend` (разбор удобства 28.09, №1).
    mutationFn: () => usersApi.extendAccess(effectivePick, extendDays),
    onSuccess: () => {
      setExtendErr(null);
      qc.invalidateQueries({ queryKey: ['admin', 'student', userId, 'enrollments'] });
    },
    onError: (e: unknown) => setExtendErr(humanizeError(e)),
  });

  // TЗ §3.3: просмотр данных для входа (логин).
  const credentials = useQuery({
    queryKey: ['admin', 'student', userId, 'credentials'],
    queryFn: () => usersApi.credentials(userId!),
    enabled: !!userId && showCreds,
  });

  // Тарифы платформы: нужны и для выдачи доступа, и для смены тарифа. Берём все,
  // фильтруем по марафону уже на месте — тарифов десятки, не тысячи.
  const tariffs = useQuery({
    queryKey: ['admin', 'tariffs'],
    queryFn: () => coursesApi.listTariffs(),
    // В приложении блока тарифа нет — и список не запрашиваем.
    enabled: !!userId && !!user.data && !isCurator && !IS_APP_BUILD,
  });

  // Расход на ученика (только админ): фактическая активность + себестоимость AI.
  const usage = useQuery({
    queryKey: ['admin', 'student', userId, 'usage'],
    queryFn: () => usersApi.getStudentUsage(userId!),
    enabled: !!userId && !!user.data && !isCurator,
  });

  // TЗ §3.3: отправить приглашение по почте (генерирует временный пароль).
  const sendInvite = useMutation({
    mutationFn: () => usersApi.sendInvite(userId!),
    onSuccess: (data) => setInvite({ email: data.email, password: data.password }),
  });

  // Код для входа. Отдельная кнопка рядом со сбросом пароля: у ученика,
  // зарегистрировавшегося самостоятельно, пароля нет вовсе, и «сбросить пароль»
  // ему бесполезен — он всё равно входит кодом.
  const [passwordOpen, setPasswordOpen] = useState(false);

  // Выданный платформой пароль для карточки. Отдельным запросом, а не полем
  // ученика: пароль — чувствительное значение, и таскать его в каждом списке
  // учеников незачем.
  const issuedPassword = useQuery({
    queryKey: ['admin', 'issued-password', userId],
    queryFn: () => usersApi.getIssuedPassword(userId!),
    enabled: !!userId,
  });

  const reissue = useMutation({
    mutationFn: () => usersApi.reissuePassword(userId!),
    onSuccess: () => {
      // Перечитываем, а не подставляем ответ: так в карточке гарантированно
      // окажется то, что действительно лежит на сервере.
      void issuedPassword.refetch();
    },
  });
  /**
   * Снятие временной блокировки входа.
   *
   * ⚠️ БЛОКИРОВКА — ЗАЩИТА ОТ ПОДБОРА, А НЕ НАКАЗАНИЕ. Пять неверных попыток
   * закрывают вход на четверть часа. Когда куратор уже понял причину и
   * продиктовал верный пароль, держать человека взаперти незачем: он в это
   * время просто не учится. Заказчик 08.09.2026 попросил такую кнопку после
   * случая, когда ученица упёрлась в блокировку из-за пароля, который у нас
   * же в карточке лежал неверный.
   */
  const unlock = useMutation({
    mutationFn: () => usersApi.unlockLogin(userId!),
  });
  const [newPassword, setNewPassword] = useState('');

  const sendCode = useMutation({
    mutationFn: () => usersApi.sendLoginCode(userId!),
    onSuccess: () => {
      setInvite(null);
      setCodeSent(true);
    },
  });

  // Смена тарифа у существующего зачисления. Срок не трогаем (0): оплаченный
  // до июня доступ не должен обнуляться от перевода на другой тариф.
  const changeTariff = useMutation({
    mutationFn: () => usersApi.setEnrollmentTariff(effectivePick, tariffPick, 0),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'student', userId, 'enrollments'] });
    },
  });

  // Задать конкретный пароль. Нужно там, где кодом из письма не обойтись:
  // демо-доступ для проверяющего в магазине приложений, служебные аккаунты.
  const setPassword = useMutation({
    mutationFn: (value: string) => usersApi.setUserPassword(userId!, value),
    onSuccess: () => {
      setPasswordOpen(false);
      setNewPassword('');
    },
  });

  const remove = useMutation({
    mutationFn: () => usersApi.remove(userId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'students'] });
      navigate('/admin/students');
    },
  });

  if (user.isLoading) return <Card variant="subtle">{t('common.loading')}</Card>;
  if (!user.data) return <Card variant="subtle" className="text-error">{t('adminDetail.notFound')}</Card>;

  const u = user.data;
  const sections = detailSections(u.role);
  const enrollList = enrollments.data ?? [];
  const marathonName = (id: string) => marathonMap.get(id) ?? t('adminDetail.marathonFallback', { id: id.slice(0, 8) });

  // Default-pick the only or first enrollment so the admin doesn't have to
  // open the dropdown for the common case (1 marathon per student).
  const effectivePick = pickedEnrollmentId(pickedEnrollment, enrollList);
  const pickedRow = enrollList.find((e) => e.id === effectivePick);
  const extendDays =
    extendTyped && extendTyped.id === effectivePick
      ? extendTyped.days
      : defaultExtendDays(pickedRow, pickedRow ? marathonCategory.get(pickedRow.marathon_id) : null);
  const extendSkipped = extrasSkippedReasons(extend.data?.extras_skipped);

  // Курс ₸/$ для читаемости — та же прикидка, что в экономической таблице.
  const KZT_RATE = 500;
  const usd = (n: number) => `$${n.toFixed(n < 1 ? 3 : 2)}`;
  const kzt = (n: number) => `${Math.round(n * KZT_RATE).toLocaleString('ru-RU')} ₸`;
  const usg = usage.data;

  const askBlock = async () => {
    if (await ask({ message: t('adminStudents.confirmBlock', { name: u.full_name }), danger: true })) block.mutate();
  };
  const askUnblock = async () => {
    if (await ask({ message: t('adminStudents.confirmUnblock', { name: u.full_name }), danger: true })) unblock.mutate();
  };
  const askRemove = async () => {
    if (await ask({ message: t('adminDetail.confirmDeleteStudent', { name: u.full_name }), danger: true })) remove.mutate();
  };
  // «Задать пароль»: на телефоне кнопки внизу окна во весь экран (правило 3), на компьютере — под полем.
  const passwordButtons = (
    <div className={isMobile ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2'}>
      <Button variant="ghost" onClick={() => setPasswordOpen(false)}>
        {t('common.cancel')}
      </Button>
      <Button
        disabled={newPassword.trim().length < 8}
        loading={setPassword.isPending}
        onClick={() => setPassword.mutate(newPassword.trim())}
      >
        {t('common.save')}
      </Button>
    </div>
  );

  return (
    <>
      {isMobile ? (
        // Телефон (docs/CURATOR-MOBILE-DESIGN.md §5.6): шапка «назад · имя · почта» и «⋯» вместо строки из четырёх
        // кнопок — она и делала карточку шире экрана на 390 (замечено 28.09.2026, разбор удобства админки).
        <div className="mb-3">
          <MTitle
            back={u.role === 'curator' ? '/admin/curators' : '/admin/students'}
            title={u.full_name}
            subtitle={u.email}
            right={
              <button
                type="button"
                onClick={() => setHeadSheet(true)}
                aria-label={t('nav.more')}
                className="grid h-11 w-11 flex-none place-items-center rounded-full bg-surface-container-low text-on-surface"
              >
                <span className="material-symbols-outlined text-[20px]">more_horiz</span>
              </button>
            }
          />
          <ActionSheet
            open={headSheet}
            onClose={() => setHeadSheet(false)}
            title={u.full_name}
            actions={[
              { label: t('common.edit'), icon: 'edit', onSelect: () => setEditOpen(true) },
              u.status !== 'blocked'
                ? { label: t('adminStudents.actionBlock'), icon: 'block', danger: true, onSelect: askBlock }
                : { label: t('adminStudents.actionUnblock'), icon: 'lock_open', onSelect: askUnblock },
              { label: t('common.delete'), icon: 'delete', danger: true, onSelect: askRemove },
            ]}
          />
        </div>
      ) : (
      <PageHeader
        title={u.full_name}
        subtitle={u.email}
        actions={
          <>
            {/* Куратора открывают из списка кураторов — туда и назад (разбор удобства 28.09, п. 106). */}
            <Link to={u.role === 'curator' ? '/admin/curators' : '/admin/students'}>
              <Button variant="ghost" size="sm">{t('adminDetail.backToList')}</Button>
            </Link>
            <Button variant="secondary" onClick={() => setEditOpen(true)}>
              <span className="material-symbols-outlined">edit</span> {t('common.edit')}
            </Button>
            {u.status !== 'blocked' ? (
              <Button variant="danger" onClick={askBlock} loading={block.isPending}>
                <span className="material-symbols-outlined">block</span> {t('adminStudents.actionBlock')}
              </Button>
            ) : (
              <Button variant="secondary" onClick={askUnblock} loading={unblock.isPending}>
                <span className="material-symbols-outlined">lock_open</span> {t('adminStudents.actionUnblock')}
              </Button>
            )}
            <Button variant="danger" onClick={askRemove} loading={remove.isPending}>
              <span className="material-symbols-outlined">delete</span> {t('common.delete')}
            </Button>
          </>
        }
      />
      )}

      {(remove.error || block.error || unblock.error) && (
        <Card variant="subtle" className="mb-4 text-sm text-error">
          {humanizeError(remove.error || block.error || unblock.error)}
        </Card>
      )}

      <Card className="mb-4">
        {/* На телефоне аватар и метки — одной строкой, «Прогресс» переносится под них во всю ширину. */}
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center max-md:flex-row max-md:flex-wrap max-md:items-center">
          <Avatar name={u.full_name} src={u.avatar_url} size="lg" />
          <div className="min-w-0 flex-1">
            {/* На телефоне имя и почта уже в шапке (MTitle) — второй раз не повторяем. */}
            <div className="max-md:hidden">
              <CardTitle>{u.full_name}</CardTitle>
              <p className="text-sm text-on-surface-variant">{u.email}</p>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 max-md:mt-0">
              <Pill variant={u.status === 'active' ? 'success' : 'neutral'}>
                {STATUS_LABELS[u.status] ?? u.status}
              </Pill>
              <Pill variant="primary">{ROLE_LABELS[u.role] ?? u.role}</Pill>
              <Pill>
                {t('adminDetail.lastSeen')}: {u.last_seen_at ? new Date(u.last_seen_at).toLocaleString('ru-RU') : '—'}
              </Pill>
            </div>
          </div>
          {/* Разбор прогресса: уроки ученика и его ответы внутри урока. У куратора — нет (28.09.2026). */}
          {sections.includes('progressLink') && (
            <Link
              to={`/admin/students/${userId}/progress`}
              // На телефоне — на всю ширину, 44 px (§5.6): «Прогресс» — главное действие карточки.
              className="inline-flex flex-none items-center justify-center gap-1 rounded-pill bg-primary px-4 py-2 text-sm font-semibold text-on-primary transition hover:opacity-90 max-md:min-h-11 max-md:w-full"
            >
              <span className="material-symbols-outlined text-[18px]">trending_up</span>
              {t('studentProgress.open')}
            </Link>
          )}
        </div>
        {/* Курсы ученика со сроками, статусом записи и куратором — и кнопка переноса (28.09.2026). */}
        {sections.includes('enrollmentSummary') && enrollments.data && (
          <StudentEnrollmentsSummary user={u} enrollments={enrollList} marathons={marathonsList.data ?? []} />
        )}
      </Card>

      {/* Журнал переносов с «Откатить» — ручки только admin (договор enrollment-transfer §9). */}
      {sections.includes('transfers') && viewerRole === 'admin' && <TransferJournal studentId={userId!} />}

      <Card className="mb-4">
        <CardTitle>{t('adminDetail.contacts')}</CardTitle>
        <div className="mt-3 grid gap-2 text-sm md:grid-cols-2">
          <p><strong>{t('auth.email')}:</strong> {u.email}</p>
          <p><strong>{t('adminDetail.phone')}:</strong> {u.phone ?? '—'}</p>
          <p><strong>{t('adminDetail.timezone')}:</strong> {u.timezone ?? '—'}</p>
          {/* Строки «Куратор» здесь больше нет (28.09.2026): карточка одного пользователя не отдаёт `curator_name`, и
              у личного ученика стояло «не назначен» при живом кураторе; у куратора — шум (разбор удобства, п. 106).
              Куратор теперь в шапке — по каждому курсу, из группы или личный. */}
          <p><strong>{t('adminDetail.registeredAt')}:</strong> {new Date(u.created_at).toLocaleDateString('ru-RU')}</p>
          <p><strong>{t('adminDetail.lastSeen')}:</strong> {u.last_seen_at ? new Date(u.last_seen_at).toLocaleString('ru-RU') : '—'}</p>
        </div>
      </Card>

      {/* Баллы ученика (12.09.2026, договор `student-points.md`). Только у роли
          `student`: у сотрудника баллов нет, и ручка ответила бы 403 — показывать
          отказ там, где данных не бывает по природе, значит пугать зря. */}
      {sections.includes('points') && (
        <div className="mb-4">
          <StudentPointsCard studentId={userId!} />
        </div>
      )}

      {/* Откуда ученик входил (D41). Тоже только у ученика: сервер отвечает 404
          на сотрудника — сотрудников этим списком не разглядывают. */}
      {sections.includes('devices') && (
        <div className="mb-4">
          <StudentDevicesCard studentId={userId!} />
        </div>
      )}

      {/* Подтипы марафонов куратора (D40): без них куратор не видит ни курсов,
          ни учеников. У остальных ролей блока нет — подтипы ставятся куратору. */}
      {sections.includes('curatorSubtypes') && <CuratorSubtypesCard userId={userId!} />}

      {/* Карточка куратора (28.09.2026): сводка, его ученики, лента проверок — вместо ученических блоков. */}
      {sections.includes('curatorCard') && <CuratorCard curatorId={userId!} />}

      <Card className="mb-4">
        <CardTitle>{t('adminDetail.loginData')}</CardTitle>
        <div className="mt-3 space-y-3 text-sm">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => setShowCreds((v) => !v)}>
              <span className="material-symbols-outlined">visibility</span>
              {showCreds ? t('adminDetail.hideLogin') : t('adminDetail.showLogin')}
            </Button>
            {/* «Выслать код» первой и основной кнопкой: у ученика, который
                зарегистрировался сам, пароля нет вовсе — сбрасывать нечего. */}
            <Button size="sm" onClick={() => sendCode.mutate()} loading={sendCode.isPending}>
              <span className="material-symbols-outlined">mail</span>
              {t('adminDetail.sendLoginCode')}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={async () => {
                // Спрашиваем подтверждение: действие необратимо и ломает вход
                // человеку, который сейчас, возможно, сидит на уроке.
                if (await ask({
                  title: t('adminDetail.resetPasswordTitle'),
                  message: t('adminDetail.resetPasswordWarn'),
                  confirmLabel: t('adminDetail.resetPassword'),
                  danger: true,
                })) {
                  setCodeSent(false);
                  sendInvite.mutate();
                }
              }}
              loading={sendInvite.isPending}
            >
              <span className="material-symbols-outlined">vpn_key</span>
              {t('adminDetail.resetPassword')}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setPasswordOpen(true)}>
              <span className="material-symbols-outlined">lock</span>
              {t('adminDetail.setPassword')}
            </Button>
          </div>

          <Modal
            open={passwordOpen}
            onClose={() => setPasswordOpen(false)}
            title={t('adminDetail.setPassword')}
            size="sm"
            phoneFull
            footer={isMobile ? passwordButtons : undefined}
          >
            <p className="text-sm text-on-surface-variant">{t('adminDetail.setPasswordHint')}</p>
            <div className="mt-4">
              <Input
                type="password"
                label={t('profile.newPassword')}
                hint={t('profile.passwordHint')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            {setPassword.error && (
              <p className="mt-3 text-sm text-error">{humanizeError(setPassword.error)}</p>
            )}
            {!isMobile && <div className="mt-6">{passwordButtons}</div>}
          </Modal>

          {/*
            Пароль, ВЫДАННЫЙ платформой, показывается: администратор диктует его
            по телефону, когда письмо ушло в спам. Пароль, придуманный самим
            человеком, не покажет никто — в базе от него односторонний
            отпечаток, и тогда здесь стоит объяснение причины, а не пустое поле.
          */}
          {issuedPassword.isLoading && (
            <p className="text-xs text-on-surface-variant">{t('common.loading')}</p>
          )}
          {issuedPassword.data?.available && issuedPassword.data.password && (
            <div className="rounded-xl border border-outline-variant bg-surface-variant/40 p-3">
              <p className="text-xs uppercase tracking-wide text-on-surface-variant">
                {t('adminDetail.issuedPasswordTitle')}
              </p>
              {/* break-all: пароль куратора — 21 знак, с разрядкой он выталкивал карточку на 390 за край на 20 px
                  (кадр 6б, 13.10.2026); у ученика шесть цифр — там не видно. */}
              <p className="mt-1 break-all text-xl font-bold tracking-[0.3em] tabular-nums">
                {issuedPassword.data.password}
              </p>
              <p className="mt-2 text-xs text-on-surface-variant">
                {t('adminDetail.issuedPasswordHint')}
              </p>
            </div>
          )}
          {issuedPassword.data && !issuedPassword.data.available && (
            <p className="text-xs text-on-surface-variant">
              {issuedPassword.data.reason || t('adminDetail.passwordNotVisible')}
            </p>
          )}

          <div className="flex flex-col items-start">
            <Button
              size="sm"
              variant="ghost"
              loading={unlock.isPending}
              onClick={() => unlock.mutate()}
            >
              {unlock.isSuccess ? t('adminDetail.unlockDone') : t('adminDetail.unlockLogin')}
            </Button>
            <p className="mt-1 text-xs text-on-surface-variant">{t('adminDetail.unlockHint')}</p>
          </div>

          <Button
            size="sm"
            variant="secondary"
            loading={reissue.isPending}
            onClick={async () => {
              // Подтверждение обязательно: прежний код перестаёт работать
              // сразу, а ученик может в эту минуту сидеть на уроке.
              if (
                await ask({
                  title: t('adminDetail.reissueTitle'),
                  message: t('adminDetail.reissueBody'),
                  confirmLabel: t('adminDetail.reissueConfirm'),
                })
              ) {
                reissue.mutate();
              }
            }}
          >
            <span className="material-symbols-outlined">vpn_key</span>
            {t('adminDetail.reissue')}
          </Button>

          {sendCode.error && <p className="text-sm text-error">{humanizeError(sendCode.error)}</p>}
          {codeSent && !sendCode.error && (
            <p className="text-sm text-success">{t('adminDetail.loginCodeSent')}</p>
          )}

          {showCreds && (
            credentials.isLoading ? (
              <p className="text-on-surface-variant">{t('common.loading')}</p>
            ) : credentials.data ? (
              <p>
                <strong>{t('adminDetail.loginEmail')}:</strong> {credentials.data.email}
              </p>
            ) : (
              <p className="text-error">{t('adminDetail.credentialsFailed')}</p>
            )
          )}

          {sendInvite.error && (
            <p className="text-sm text-error">{humanizeError(sendInvite.error)}</p>
          )}

          {invite && (
            <div className="rounded-card bg-surface-container-highest p-3">
              <p className="mb-1 text-xs uppercase tracking-label text-on-surface-variant">
                {t('adminDetail.inviteSentTitle')}
              </p>
              <p><strong>{t('auth.email')}:</strong> {invite.email}</p>
              <p><strong>{t('adminDetail.newPassword')}:</strong> <code className="font-mono">{invite.password}</code></p>
              <p className="mt-1 text-xs text-on-surface-variant">
                {t('adminDetail.inviteNote')}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Расход на ученика — только админ. Себестоимость AI + активность.
          В приложении блок скрыт: это суммы, а сумм внутри приложения быть не
          должно нигде. Себестоимость — не цена товара, но объяснять эту разницу
          ревьюеру Apple посреди отказа дороже, чем открыть тот же экран в браузере. */}
      {!isStoreBuild() && sections.includes('usage') && (
      <Card className="mb-4">
        <div className="flex items-center justify-between gap-3">
          <CardTitle>{t('adminUsage.title')}</CardTitle>
          <Pill className="bg-surface-container-highest text-on-surface-variant">
            <span className="material-symbols-outlined text-[14px]">lock</span>
            {t('adminUsage.adminOnly')}
          </Pill>
        </div>

        {usage.isLoading ? (
          <p className="mt-3 text-sm text-on-surface-variant">{t('common.loading')}</p>
        ) : !usg ? (
          <p className="mt-3 text-sm text-on-surface-variant">{t('adminUsage.noData')}</p>
        ) : (
          <div className="mt-4 space-y-4">
            {/* Итоговая себестоимость */}
            <div className="rounded-card bg-gradient-to-br from-primary to-primary-pressed p-4 text-on-primary">
              <p className="text-xs uppercase tracking-label opacity-90">{t('adminUsage.totalTitle')}</p>
              <p className="mt-1 font-display text-3xl font-bold tabular-nums tracking-display">
                {usd(usg.total_cost_usd)}
              </p>
              <p className="text-sm opacity-90 tabular-nums">≈ {kzt(usg.total_cost_usd)}</p>
            </div>

            {/* Разбивка по активности */}
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-card bg-surface-container-highest p-3">
                <div className="flex items-center gap-1.5 text-on-surface-variant">
                  <span className="material-symbols-outlined text-[18px]">graphic_eq</span>
                  <span className="text-xs">{t('adminUsage.speaking')}</span>
                </div>
                <p className="mt-1 text-xl font-semibold tabular-nums">{usg.speakings}</p>
                <p className="text-xs text-on-surface-variant tabular-nums">
                  {usg.speaking_minutes} {t('adminUsage.min')} · {usd(usg.speaking_cost_usd)}
                </p>
              </div>

              {/* Письмо — вторая половина того, что относим на ученика. */}
              <div className="rounded-card bg-surface-container-highest p-3">
                <div className="flex items-center gap-1.5 text-on-surface-variant">
                  <span className="material-symbols-outlined text-[18px]">edit_note</span>
                  <span className="text-xs">{t('adminUsage.writing')}</span>
                </div>
                <p className="mt-1 text-xl font-semibold tabular-nums">{usg.writings_checked}</p>
                <p className="text-xs text-on-surface-variant tabular-nums">
                  {usd(usg.writing_cost_usd)}
                </p>
              </div>

              {/*
                Спринты и словарь — БЕЗ ДЕНЕГ, только количеством. Разбор слова
                и сборка задания оплачиваются один раз на всю платформу: второму
                ученику то же самое достаётся бесплатно, и относить это на
                конкретного человека неверно.
              */}
              <div className="rounded-card bg-surface-container-highest p-3">
                <div className="flex items-center gap-1.5 text-on-surface-variant">
                  <span className="material-symbols-outlined text-[18px]">menu_book</span>
                  <span className="text-xs">{t('adminUsage.activity')}</span>
                </div>
                <p className="mt-1 text-xl font-semibold tabular-nums">{usg.words}</p>
                <p className="text-xs text-on-surface-variant tabular-nums">
                  {t('adminUsage.learned', { n: usg.words_learned })} ·{' '}
                  {t('adminUsage.sprintsN', { n: usg.sprints })}
                </p>
              </div>
            </div>

            <p className="text-xs leading-relaxed text-on-surface-variant">
              {t('adminUsage.note')}
              {usg.cost_tracked_from
                ? ' ' + t('adminUsage.trackedFrom', { date: usg.cost_tracked_from })
                : ' ' + t('adminUsage.noSpendYet')}
            </p>
          </div>
        )}
      </Card>
      )}

      {sections.includes('access') && (
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <CardTitle>{t('adminDetail.accessTitle')}</CardTitle>
          <Button size="sm" onClick={() => setGrantOpen(true)}>
            <span className="material-symbols-outlined">add</span>
            {t('adminDetail.grantAccess')}
          </Button>
        </div>
        {enrollments.isLoading ? (
          <p className="mt-3 text-sm text-on-surface-variant">{t('adminDetail.loadingCourses')}</p>
        ) : enrollList.length === 0 ? (
          <p className="mt-3 text-sm text-on-surface-variant">
            {t('adminDetail.noEnrollments')}
          </p>
        ) : (
          <div className="mt-3 space-y-2">
            {enrollList.length > 1 && (
              <label className="block text-sm">
                {t('adminDetail.course')}
                <select
                  value={effectivePick}
                  onChange={(e) => setPickedEnrollment(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-outline bg-surface px-3 py-2 text-sm"
                >
                  {enrollList.map((e) => (
                    <option key={e.id} value={e.id}>
                      {marathonName(e.marathon_id)} · {t('adminDetail.accessUntil')} {new Date(e.access_until).toLocaleDateString('ru-RU')} · {e.status}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {enrollList.length === 1 && (
              <p className="text-xs text-on-surface-variant">
                {marathonName(enrollList[0].marathon_id)} · {t('adminDetail.accessUntil')}{' '}
                {new Date(enrollList[0].access_until).toLocaleDateString('ru-RU')}
              </p>
            )}
            {/* Смена тарифа у выбранного зачисления. Показываем только тарифы
                ЭТОГО марафона: чужой тариф выдал бы возможности другого курса,
                и сервер такой запрос всё равно отклонит. */}
            {(() => {
              // ⚠️ В ПРИЛОЖЕНИИ ТАРИФА НЕТ НИ СЛОВОМ (решение владельца 13.10.2026): блок только на сайте, строки
              // с «тариф» вырезаются из сборки приложения (vite.config.ts), сторож — dist-mobile-guard.sh раздел 10.
              if (IS_APP_BUILD) return null;
              const current = enrollList.find((e) => e.id === effectivePick);
              const forMarathon = (tariffs.data ?? []).filter(
                (tp) => tp.is_active && tp.marathon_id === current?.marathon_id,
              );
              if (!current || forMarathon.length === 0) return null;
              const currentTariff = forMarathon.find((tp) => tp.id === current.tariff_plan_id);
              return (
                <div className="rounded-card bg-surface-container-highest p-3">
                  <p className="text-xs uppercase tracking-label text-on-surface-variant">
                    {t('adminDetail.tariffTitle')}
                  </p>
                  <p className="mt-1 text-sm">
                    {currentTariff ? currentTariff.title : t('adminDetail.tariffNone')}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <select
                      value={tariffPick || current.tariff_plan_id || ''}
                      onChange={(e) => setTariffPick(e.target.value)}
                      className="min-w-[12rem] flex-1 rounded-lg border border-outline bg-surface px-3 py-2 text-sm"
                    >
                      <option value="">{t('adminDetail.tariffPick')}</option>
                      {forMarathon.map((tp) => (
                        <option key={tp.id} value={tp.id}>
                          {tp.title} · {t('adminDetail.tariffDays', { count: tp.duration_days })}
                        </option>
                      ))}
                    </select>
                    <Button
                      variant="secondary"
                      onClick={() => changeTariff.mutate()}
                      loading={changeTariff.isPending}
                      disabled={!tariffPick || tariffPick === current.tariff_plan_id}
                    >
                      {t('adminDetail.tariffApply')}
                    </Button>
                  </div>
                  {changeTariff.error && (
                    <p className="mt-2 text-xs text-error">{humanizeError(changeTariff.error)}</p>
                  )}
                  {changeTariff.isSuccess && !changeTariff.error && (
                    // Про минуту говорим честно: набор возможностей кешируется,
                    // иначе менеджер решит, что смена не сработала, и нажмёт ещё раз.
                    <p className="mt-2 text-xs text-success">{t('adminDetail.tariffChanged')}</p>
                  )}
                </div>
              );
            })()}

            {/*
              Группа и куратор.

              ⚠️ КУРАТОР НАЗНАЧАЕТСЯ ЧЕРЕЗ ГРУППУ, а не ученику напрямую: у
              группы кураторов может быть несколько, и отдельное поле «куратор
              ученика» разошлось бы с составом группы уже на второй неделе.
              Поэтому здесь выбирается группа, а кураторы показываются те, что
              у неё есть.
            */}
            {(() => {
              const current = enrollList.find((e) => e.id === effectivePick);
              if (!current) return null;
              return (
                <GroupPicker
                  enrollmentId={current.id}
                  marathonId={current.marathon_id}
                  groupId={current.group_id ?? null}
                  onChanged={() =>
                    qc.invalidateQueries({ queryKey: ['admin', 'student', userId, 'enrollments'] })
                  }
                />
              );
            })()}

            {/* Продление — только admin (`/admin/enrollments/:id/extend`, adminWrite): менеджеру кнопка давала бы 403. */}
            {viewerRole === 'admin' && (
            <>
            <p className="pt-1 text-xs uppercase tracking-label text-on-surface-variant">
              {t('adminDetail.extendAccess')}
            </p>
            {/* На телефоне столбиком: поле и «Продлить на N дней» рядом не влезают в 390. */}
            <div className="flex gap-2 max-md:flex-col">
              <Input
                type="number"
                min={1}
                value={extendDays > 0 ? extendDays : ''}
                onChange={(e) => setExtendTyped({ id: effectivePick, days: Number(e.target.value) })}
              />
              <Button
                onClick={() => extend.mutate()}
                loading={extend.isPending}
                disabled={!effectivePick || extendDays <= 0}
              >
                {(() => {
                  const label = extendButtonLabel(extendDays);
                  return t(label.key, { count: label.count });
                })()}
              </Button>
            </div>
            {extendErr && <p className="text-xs text-error">{extendErr}</p>}
            {extend.isSuccess && !extendErr && <p className="text-xs text-success">{t('adminDetail.accessExtended')}</p>}
            {/* Доп. курс, не пошедший за основным (11.10.2026): какой и почему — иначе админ узнал бы от ученика. */}
            {extend.isSuccess &&
              extendSkipped.map((s) => (
                <p key={s.marathonId} className="text-xs text-error">
                  {t('adminDetail.extraSkipped', { course: marathonName(s.marathonId), reason: t(s.key) })}
                </p>
              ))}
            </>
            )}
          </div>
        )}
      </Card>
      )}

      {grantOpen && (
        <GrantAccessModal
          open={grantOpen}
          onClose={() => setGrantOpen(false)}
          studentId={userId!}
          enrolledMarathonIds={new Set(enrollList.map((e) => e.marathon_id))}
          onGranted={() => qc.invalidateQueries({ queryKey: ['admin', 'student', userId, 'enrollments'] })}
        />
      )}

      {editOpen && (
        <StudentFormModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          initial={u}
          onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'student'] })}
        />
      )}
    </>
  );
}

/**
 * «Выдать доступ» — зачисление ученика в марафон с выбором тарифа и срока.
 *
 * Зачем появилось (21.08.2026): раньше тариф выставлялся ТОЛЬКО в момент
 * создания ученика админом. После перехода на самостоятельную регистрацию
 * (имя, почта, телефон, код из письма) таких учеников стало большинство, и
 * выдать им тариф из кабинета было нечем — оставалось лишь массовое зачисление
 * из марафона, которое тариф не ставит вовсе.
 */
function GrantAccessModal({
  open,
  onClose,
  studentId,
  enrolledMarathonIds,
  onGranted,
}: {
  open: boolean;
  onClose: () => void;
  studentId: string;
  enrolledMarathonIds: Set<string>;
  onGranted: () => void;
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [marathonId, setMarathonId] = useState('');
  const [tariffId, setTariffId] = useState('');
  const [days, setDays] = useState(0);

  const marathons = useQuery({
    queryKey: ['admin', 'marathons', 'for-grant'],
    queryFn: () => coursesApi.listAllMarathons({ limit: 100, template: 'false' }),
    enabled: open,
  });
  const tariffs = useQuery({
    queryKey: ['admin', 'tariffs'],
    queryFn: () => coursesApi.listTariffs(),
    enabled: open && !IS_APP_BUILD,
  });

  // Марафоны, где ученик ещё не числится: повторное зачисление сервер молча
  // вернёт как существующее, и менеджер решит, что доступ выдан заново.
  const available = (marathons.data?.items ?? []).filter((m) => !enrolledMarathonIds.has(m.id));
  const forMarathon = (tariffs.data ?? []).filter((tp) => tp.is_active && tp.marathon_id === marathonId);
  // Срок по умолчанию — по курсу (решение владельца 11.10.2026): 90 дней, «один на один» — 365; доп. курсы
  // сервер выдаёт с датами этой записи. Поле правится руками; не тронуто — срок не шлём, решает сервер тем же
  // правилом. До 11.10 здесь брался срок тарифа или 365.
  const pickedCourse = available.find((m) => m.id === marathonId);
  const effectiveDays = days > 0 ? days : accessDefaultDays(pickedCourse?.access_category);

  const grant = useMutation({
    mutationFn: () =>
      usersApi.enroll({
        student_id: studentId,
        marathon_id: marathonId,
        // План не выбран — поля нет. Пустая строка давала 400 «invalid UUID length: 0»: выдать доступ к курсу без
        // тарифов было нельзя вовсе (с 24.08, найдено 11.10.2026 на стенде, техдолг п. 398).
        tariff_plan_id: tariffId || undefined,
        duration_days: days > 0 ? days : undefined,
      }),
    onSuccess: () => {
      onGranted();
      onClose();
    },
  });

  // На телефоне — внизу окна во весь экран (правило 3), на компьютере — под полями, как было.
  const buttons = (
    <div className={isMobile ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2 pt-2'}>
      <Button variant="ghost" onClick={onClose} disabled={grant.isPending}>
        {t('common.cancel')}
      </Button>
      <Button onClick={() => grant.mutate()} loading={grant.isPending} disabled={!marathonId || effectiveDays <= 0}>
        {t('adminDetail.grantSubmit')}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('adminDetail.grantAccess')}
      size="sm"
      phoneFull
      footer={isMobile ? buttons : undefined}
    >
      <div className="space-y-3">
        <label className="block text-sm">
          {t('adminDetail.course')}
          <select
            value={marathonId}
            onChange={(e) => {
              setMarathonId(e.target.value);
              setTariffId('');
            }}
            className="mt-1 w-full rounded-lg border border-outline bg-surface px-3 py-2 text-sm"
          >
            <option value="">{t('adminDetail.coursePick')}</option>
            {available.map((m) => (
              <option key={m.id} value={m.id}>{m.title}</option>
            ))}
          </select>
        </label>

        {/* Выбор тарифа — только на сайте (решение владельца 13.10.2026: в приложении ни слова «тариф»). Без него
            доступ выдаётся так же: тариф необязателен и доступ не урезает. */}
        {marathonId && !IS_APP_BUILD && (
          <label className="block text-sm">
            {t('adminDetail.tariffTitle')}
            <select
              value={tariffId}
              onChange={(e) => setTariffId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-outline bg-surface px-3 py-2 text-sm"
            >
              <option value="">{t('adminDetail.tariffPick')}</option>
              {forMarathon.map((tp) => (
                <option key={tp.id} value={tp.id}>
                  {tp.title} · {t('adminDetail.tariffDays', { count: tp.duration_days })}
                </option>
              ))}
            </select>
            {forMarathon.length === 0 && (
              // Без тарифа зачислить всё равно можно — и это НЕ урезает доступ:
              // платформа ограничивает только явным списком возможностей.
              <p className="mt-1 text-xs text-on-surface-variant">{t('adminDetail.noTariffsHint')}</p>
            )}
          </label>
        )}

        <label className="block text-sm">
          {t('adminDetail.accessDays')}
          <Input
            type="number"
            min={1}
            value={effectiveDays}
            onChange={(e) => setDays(Number(e.target.value))}
          />
        </label>

        {grant.error && <p className="text-sm text-error">{humanizeError(grant.error)}</p>}

        {!isMobile && buttons}
      </div>
    </Modal>
  );
}


/**
 * Группа ученика в одном курсе — и кураторы этой группы.
 *
 * ⚠️ ГРУППЫ СОЗДАЮТСЯ НЕ ЗДЕСЬ, а в разделе «Группы» и на странице курса. Здесь
 * только выбор из готовых: заводить группу из карточки одного ученика значило бы
 * плодить группы по одной на человека, а нужны потоки по 10–25.
 */
function GroupPicker({
  enrollmentId,
  marathonId,
  groupId,
  onChanged,
}: {
  enrollmentId: string;
  marathonId: string;
  groupId: string | null;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [pick, setPick] = useState('');

  const groupsQ = useQuery({
    queryKey: ['admin', 'marathon', marathonId, 'groups'],
    queryFn: () => usersApi.listGroups(marathonId),
    enabled: !!marathonId,
  });
  const groups = groupsQ.data?.items ?? [];
  const current = groups.find((g) => g.id === groupId) ?? null;

  const move = useMutation({
    mutationFn: async () => {
      if (pick) await usersApi.moveToGroup(enrollmentId, groupId, pick);
    },
    onSuccess: () => { setPick(''); onChanged(); },
  });

  const clear = useMutation({
    mutationFn: () => usersApi.removeGroupMembers(groupId!, [enrollmentId]),
    onSuccess: onChanged,
  });

  return (
    <div className="rounded-card bg-surface-container-highest p-3">
      <p className="text-xs uppercase tracking-label text-on-surface-variant">
        {t('adminDetail.groupTitle')}
      </p>
      <p className="mt-1 text-sm">{current ? current.name : t('adminDetail.groupNone')}</p>

      {current && (
        <p className="mt-1 text-xs text-on-surface-variant">
          {current.curators.length > 0
            ? `${t('adminDetail.groupCurators')}: ${current.curators.map((c) => c.name).join(', ')}`
            : t('adminDetail.groupNoCurators')}
        </p>
      )}

      {groups.length === 0 ? (
        <p className="mt-2 text-xs text-warning">{t('adminDetail.groupNoneInCourse')}</p>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          <select
            value={pick || groupId || ''}
            onChange={(e) => setPick(e.target.value)}
            className="min-w-[12rem] flex-1 rounded-lg border border-outline bg-surface px-3 py-2 text-sm"
          >
            <option value="">{t('adminDetail.groupPick')}</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name} · {g.member_count}/{g.capacity}
              </option>
            ))}
          </select>
          <Button
            variant="secondary"
            onClick={() => move.mutate()}
            loading={move.isPending}
            disabled={!pick || pick === groupId}
          >
            {t('adminDetail.groupApply')}
          </Button>
          {groupId && (
            <Button variant="ghost" onClick={() => clear.mutate()} loading={clear.isPending}>
              {t('adminDetail.groupRemove')}
            </Button>
          )}
        </div>
      )}
      {(move.error || clear.error) && (
        // Свой текст по коду (409 not_in_group — техдолг 183), иначе общий.
        <p className="mt-2 text-xs text-error">{lifecycleErrorText(move.error ?? clear.error, t)}</p>
      )}
    </div>
  );
}
